import { Button, NativeSelect, Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@ondit/ui";
import { useState } from "react";
import { Link, useParams } from "react-router";
import {
	Async,
	Badge,
	Empty,
	Json,
	KV,
	PageHead,
	Pager,
	SearchBox,
	Segmented,
	Status,
	useAction,
	useConfirm,
} from "@/components/ui";
import { useI18n, useT } from "@/i18n";
import { api, type Job, type JobDetail } from "@/lib/api";
import * as fmt from "@/lib/format";
import { useAsync, useDebounced, usePaged } from "@/lib/hooks";

export function JobsPage() {
	const t = useT();
	const { locale } = useI18n();
	const [q, setQ] = useState("");
	const [status, setStatus] = useState<"" | "queued" | "running" | "failed" | "skipped" | "cancelled" | "done">(
		"failed",
	);
	const [kind, setKind] = useState("");
	const query = useDebounced(q);
	const { state, rows, hasMore, loadingMore, more } = usePaged(
		(offset, limit) => api.jobs({ q: query, status, kind, limit, offset }),
		(page) => page.jobs,
		[query, status, kind],
	);
	const { run, busy } = useAction();
	const confirm = useConfirm();

	return (
		<>
			<PageHead title={t("jobs.title")} help={t("jobs.lede")} />
			<div className="table-tools">
				<SearchBox value={q} onChange={setQ} />
				<Segmented
					value={status}
					onChange={setStatus}
					options={[
						{ value: "failed", label: t("jobs.status.failed") },
						{ value: "queued", label: t("jobs.status.queued") },
						{ value: "running", label: t("jobs.status.running") },
						{ value: "skipped", label: t("jobs.status.skipped") },
						{ value: "cancelled", label: t("jobs.status.cancelled") },
						{ value: "done", label: t("jobs.status.done") },
						{ value: "", label: t("state.all") },
					]}
				/>
				{state.data ? (
					<NativeSelect aria-label={t("jobs.kind")} value={kind} onChange={(e) => setKind(e.target.value)}>
						<option value="">{t("state.all")}</option>
						{[...new Set(state.data.counts.map((c) => c.kind))].map((k) => (
							<option key={k} value={k}>
								{k}
							</option>
						))}
					</NativeSelect>
				) : null}
			</div>
			<Async state={state}>
				{({ counts }) => (
					<>
						<details className="queue-summary">
							<summary>{t("jobs.breakdown")}</summary>
							<div className="chips">
								{counts.map((c) => (
									<Badge key={`${c.kind}:${c.status}`} tone="outline">
										{c.kind} · {t(`jobs.status.${c.status}`)} · {c.count}
									</Badge>
								))}
							</div>
						</details>
						{rows.length === 0 ? (
							<Empty />
						) : (
							<>
								<div className="table-scroll">
									<Table className="jobs-table text-[length:var(--t-sm)]">
										<TableHeader>
											<TableRow>
												<TableHead>{t("jobs.job")}</TableHead>
												<TableHead>{t("state.status")}</TableHead>
												<TableHead>{t("orgs.name")}</TableHead>
												<TableHead className="num text-right whitespace-nowrap">{t("jobs.attempts")}</TableHead>
												<TableHead>{t("jobs.error")}</TableHead>
												<TableHead>{t("jobs.runAfter")}</TableHead>
												<TableHead>{t("state.actions")}</TableHead>
											</TableRow>
										</TableHeader>
										<TableBody>
											{rows.map((j: Job) => (
												<TableRow key={j.id}>
													<TableCell>
														<Link to={`/jobs/${j.id}`} className="mono">
															{j.kind}
														</Link>
														<span className="sub mono cell-summary" title={j.key}>
															{j.key}
														</span>
													</TableCell>
													<TableCell>
														<Status value={j.status} prefix="jobs.status" />
													</TableCell>
													<TableCell>
														{j.org_id ? (
															<Link className="cell-summary" title={j.org_name ?? j.org_id} to={`/orgs/${j.org_id}`}>
																{j.org_name ?? j.org_id}
															</Link>
														) : (
															"—"
														)}
														{j.entity_name ? <span className="sub">{j.entity_name}</span> : null}
													</TableCell>
													<TableCell className="num text-right whitespace-nowrap">
														{j.attempts}/{j.max_attempts}
													</TableCell>
													<TableCell className="small" title={j.last_error ?? undefined}>
														<span className="cell-summary">{j.last_error ?? "—"}</span>
													</TableCell>
													<TableCell className="whitespace-nowrap">{fmt.relative(locale, j.run_after)}</TableCell>
													<TableCell>
														{j.status === "failed" || j.status === "skipped" || j.status === "cancelled" ? (
															<Button
																variant="default"
																size="sm"
																type="button"
																disabled={busy}
																onClick={() =>
																	run(
																		() => api.retryJob(j.id),
																		() => t("jobs.retried"),
																	).then(state.reload)
																}
															>
																{t("jobs.retry")}
															</Button>
														) : null}
														{j.status === "queued" ? (
															<Button
																variant="destructive"
																size="sm"
																type="button"
																disabled={busy}
																onClick={async () => {
																	if (!(await confirm(t("jobs.cancel"), t("jobs.cancelConfirm"), true))) return;
																	run(
																		() => api.cancelJob(j.id),
																		() => t("jobs.cancelled"),
																	).then(state.reload);
																}}
															>
																{t("jobs.cancel")}
															</Button>
														) : null}
													</TableCell>
												</TableRow>
											))}
										</TableBody>
									</Table>
								</div>
								<Pager shown={rows.length} hasMore={hasMore} busy={loadingMore} onMore={more} />
							</>
						)}
					</>
				)}
			</Async>
		</>
	);
}

export function JobPage() {
	const { id = "" } = useParams();
	const t = useT();
	const { locale } = useI18n();
	const state = useAsync<JobDetail>(() => api.job(id), [id]);
	const { run, busy } = useAction();
	const confirm = useConfirm();

	return (
		<Async state={state}>
			{(d) => {
				const j = d.job;
				return (
					<>
						<PageHead
							title={<span className="mono">{j.kind}</span>}
							lede={<span className="mono">{j.key}</span>}
							crumbs={[{ to: "/jobs", label: t("jobs.title") }]}
						>
							{j.status === "failed" || j.status === "skipped" || j.status === "cancelled" ? (
								<Button
									variant="primary"
									type="button"
									disabled={busy}
									onClick={() =>
										run(
											() => api.retryJob(j.id),
											() => t("jobs.retried"),
										).then(state.reload)
									}
								>
									{t("jobs.retry")}
								</Button>
							) : null}
							{j.status === "queued" ? (
								<Button
									variant="destructive"
									type="button"
									disabled={busy}
									onClick={async () => {
										if (!(await confirm(t("jobs.cancel"), t("jobs.cancelConfirm"), true))) return;
										run(
											() => api.cancelJob(j.id),
											() => t("jobs.cancelled"),
										).then(state.reload);
									}}
								>
									{t("jobs.cancel")}
								</Button>
							) : null}
						</PageHead>
						<div className="grid-2 detail-grid">
							<div className="card">
								<KV
									items={[
										[t("state.id"), <span className="mono">{j.id}</span>],
										[t("state.status"), <Status value={j.status} prefix="jobs.status" />],
										[t("jobs.attempts"), `${j.attempts}/${j.max_attempts}`],
										[t("orgs.name"), j.org_id ? <Link to={`/orgs/${j.org_id}`}>{j.org_name ?? j.org_id}</Link> : "—"],
										[
											t("entities.name"),
											j.entity_id ? <Link to={`/entities/${j.entity_id}`}>{j.entity_name ?? j.entity_id}</Link> : "—",
										],
										[t("jobs.lane"), <span className="mono">{j.lane}</span>],
										[t("jobs.lockedBy"), j.locked_by],
										[t("jobs.runAfter"), fmt.dateTime(locale, j.run_after)],
										[t("state.created"), fmt.dateTime(locale, j.created_at)],
										[t("state.updated"), fmt.dateTime(locale, j.updated_at)],
										[t("jobs.error"), j.last_error],
									]}
								/>
							</div>
							<div className="data-section data-section-wide">
								<h2>{t("jobs.events")}</h2>
								{d.events.length === 0 ? (
									<p className="muted">{t("state.none")}</p>
								) : (
									<Table className="text-[length:var(--t-sm)]">
										<TableBody>
											{d.events.map((e, i) => (
												// biome-ignore lint/suspicious/noArrayIndexKey: an append-only log
												<TableRow key={i}>
													<TableCell className="faint small whitespace-nowrap">{fmt.dateTime(locale, e.at)}</TableCell>
													<TableCell>
														<Badge tone={e.level === "error" ? "danger" : e.level === "warn" ? "warning" : "neutral"}>
															{e.level}
														</Badge>
													</TableCell>
													<TableCell className="small">{e.message}</TableCell>
												</TableRow>
											))}
										</TableBody>
									</Table>
								)}
							</div>
							<div className="card">
								<h2>{t("jobs.payload")}</h2>
								<Json value={d.payload} />
							</div>
							<div className="card">
								<h2>{t("jobs.result")}</h2>
								<Json value={d.result} />
							</div>
						</div>
					</>
				);
			}}
		</Async>
	);
}
